import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import AdminLayout from '../../components/layout/AdminLayout'
import StatsCard from '../../components/dashboard/StatsCard'
import LineChart from '../../components/charts/LineChart'
import PieChart from '../../components/charts/PieChart'
import BarChart from '../../components/charts/BarChart'
import DateRangePicker from '../../components/ui/DateRangePicker'
import Button from '../../components/ui/Button'

export default function AnalyticsIndex({
  stats,
  leadsOverTime,
  scansOverTime,
  topQrCodes,
  leadsByType,
  leadsByStatus,
  averageProcessingTime,
  comparison,
  period,
  startDate,
  endDate
}) {
  const [selectedPeriod, setSelectedPeriod] = useState(period)

  const handlePeriodChange = (newPeriod) => {
    setSelectedPeriod(newPeriod)
    router.get('/analytics', { period: newPeriod }, { preserveState: true })
  }

  const handleCustomDateRange = (start, end) => {
    router.get('/analytics', {
      period: 'custom',
      start_date: start,
      end_date: end
    }, { preserveState: true })
  }

  const handleExport = (type) => {
    window.location.href = `/analytics/export_${type}?period=${selectedPeriod}`
  }

  return (
    <AdminLayout>
      <Head title="Analytics" />

      <div className="space-y-6">
        {/* Header avec filtres */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-600 mt-1">
              Analyse détaillée de vos performances
            </p>
          </div>

          <div className="flex gap-3 flex-wrap">
            <div className="flex gap-2">
              {['day', 'week', 'month', 'year'].map(p => (
                <button
                  key={p}
                  onClick={() => handlePeriodChange(p)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedPeriod === p
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {p === 'day' && 'Jour'}
                  {p === 'week' && 'Semaine'}
                  {p === 'month' && 'Mois'}
                  {p === 'year' && 'Année'}
                </button>
              ))}
            </div>

            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={handleCustomDateRange}
            />

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExport('leads')}>
                📥 Exporter Leads
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('scans')}>
                📥 Exporter Scans
              </Button>
            </div>
          </div>
        </div>

        {/* KPIs avec comparaison */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            title="Leads"
            value={stats.leads.total}
            subtitle={`${stats.leads.conversion_rate}% qualifiés`}
            trend={comparison.leads}
            trendLabel="vs période précédente"
            icon="users"
            color="blue"
          />

          <StatsCard
            title="Scans QR"
            value={stats.qr_codes.scans_count}
            subtitle={`${stats.qr_codes.active} codes actifs`}
            trend={comparison.scans}
            trendLabel="vs période précédente"
            icon="qrcode"
            color="green"
          />

          <StatsCard
            title="Taux de conversion"
            value={`${stats.leads.conversion_rate}%`}
            subtitle={`${stats.leads.qualified} leads qualifiés`}
            trend={comparison.conversion_rate}
            trendLabel="vs période précédente"
            icon="chart"
            color="purple"
          />

          <StatsCard
            title="Crédits utilisés"
            value={stats.financial.credits_used}
            subtitle={`Balance: ${stats.financial.current_balance}`}
            trend={comparison.credits_used}
            trendLabel="vs période précédente"
            icon="currency"
            color="yellow"
          />
        </div>

        {/* Graphiques d'évolution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Évolution des leads</h2>
            <LineChart
              data={leadsOverTime}
              color="#3B82F6"
              label="Leads"
            />
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Évolution des scans</h2>
            <LineChart
              data={scansOverTime}
              color="#10B981"
              label="Scans"
            />
          </div>
        </div>

        {/* Distributions et insights */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top QR codes */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Top sources (QR codes)</h2>
            {topQrCodes && topQrCodes.length > 0 ? (
              <BarChart data={topQrCodes} label="Leads" />
            ) : (
              <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
            )}
          </div>

          {/* Leads par type */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Leads par type</h2>
            {leadsByType && Object.keys(leadsByType).length > 0 ? (
              <PieChart
                data={leadsByType}
                colors={['#3B82F6', '#10B981', '#F59E0B']}
              />
            ) : (
              <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
            )}
          </div>

          {/* Leads par statut */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Leads par statut</h2>
            {leadsByStatus && Object.keys(leadsByStatus).length > 0 ? (
              <PieChart
                data={leadsByStatus}
                colors={['#6B7280', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444']}
              />
            ) : (
              <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
            )}
          </div>
        </div>

        {/* Métriques supplémentaires */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-2">Temps moyen de traitement</div>
            <div className="text-3xl font-bold text-gray-900">{averageProcessingTime} heures</div>
            <p className="text-xs text-gray-500 mt-2">Du lead nouveau au lead qualifié</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-2">Crédits achetés</div>
            <div className="text-3xl font-bold text-gray-900">{stats.financial.credits_purchased}</div>
            <p className="text-xs text-gray-500 mt-2">Pendant cette période</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-2">Coût moyen par lead</div>
            <div className="text-3xl font-bold text-gray-900">{stats.financial.average_lead_cost} crédits</div>
            <p className="text-xs text-gray-500 mt-2">Basé sur les leads générés</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
